import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Clock, MessageCircle, Calendar } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const fmtRemaining = (sec) => {
  if (sec <= 0) return "EXPIRED";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

const Matches = () => {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/matches");
      setMatches(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load matches.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  // Local tick to update countdown each second without re-fetching
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const adjusted = matches.map((m) => ({
    ...m,
    seconds_remaining: Math.max(0, m.seconds_remaining - tick),
  }));

  return (
    <div data-testid="matches-page">
      <p className="text-xs uppercase tracking-[0.3em] text-[#C6FF00] font-semibold mb-1">
        Your Matches
      </p>
      <h1 className="font-heading text-4xl md:text-5xl mb-8">Lock It In.</h1>

      {loading ? (
        <MatchesLoading />
      ) : adjusted.length === 0 ? (
        <MatchesEmpty />
      ) : (
        <div className="space-y-4">
          {adjusted.map((m) => (
            <Link
              key={m.id}
              to={`/app/chat/${m.id}`}
              data-testid={`match-row-${m.id}`}
              className="flex items-center gap-4 p-4 bg-[#111] border border-white/5 hover:border-[#C6FF00]/40 rounded-2xl transition-colors group"
            >
              <img
                src={
                  m.other_user.photo_url ||
                  "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=300"
                }
                alt={m.other_user.name}
                className="w-16 h-16 rounded-xl object-cover border border-white/10"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-heading text-2xl text-white">
                    {m.other_user.name}
                    {m.other_user.age ? (
                      <span className="text-neutral-400">, {m.other_user.age}</span>
                    ) : null}
                  </h3>
                </div>
                <p className="text-xs text-neutral-400 truncate">
                  {m.last_message || (m.other_user.training_disciplines || []).slice(0, 2).join(" · ")}
                </p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <MatchStatusBadge match={m} fmtRemaining={fmtRemaining} />
                <MessageCircle size={14} className="text-neutral-500 group-hover:text-[#C6FF00]" />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const MatchStatusBadge = ({ match, fmtRemaining }) => {
  if (match.status === "scheduled") {
    return (
      <span
        data-testid={`match-status-${match.id}`}
        className="px-2 py-0.5 rounded-full bg-[#C6FF00] text-[#0A0A0A] text-[10px] font-bold uppercase tracking-wider flex items-center gap-1"
      >
        <Calendar size={10} /> Booked
      </span>
    );
  }
  if (match.status === "expired" || match.seconds_remaining <= 0) {
    return (
      <span
        data-testid={`match-status-${match.id}`}
        className="px-2 py-0.5 rounded-full bg-red-500/15 border border-red-500/30 text-red-400 text-[10px] font-bold uppercase tracking-wider"
      >
        Expired
      </span>
    );
  }
  return (
    <span
      className="font-heading text-2xl text-[#C6FF00] tabular-nums leading-none"
      data-testid={`match-countdown-${match.id}`}
    >
      {fmtRemaining(match.seconds_remaining)}
    </span>
  );
};

const MatchesEmpty = () => (
  <div className="text-center py-20" data-testid="matches-empty">
    <Clock className="text-[#C6FF00] mx-auto mb-4" size={36} />
    <h2 className="font-heading text-3xl mb-2">No matches yet.</h2>
    <p className="text-sm text-neutral-400 mb-6">
      Head to Discover and start liking profiles.
    </p>
    <Link
      to="/app/discover"
      className="inline-block bg-[#C6FF00] text-[#0A0A0A] font-bold text-xs uppercase tracking-[0.25em] px-6 py-3 rounded-md"
    >
      Go Discover
    </Link>
  </div>
);

const MatchesLoading = () => (
  <div className="space-y-4">
    {[0, 1, 2].map((i) => (
      <div key={i} className="h-24 bg-[#111] rounded-2xl border border-white/5 animate-pulse" />
    ))}
  </div>
);

export default Matches;
